import type { BannerConfig, BannerImage, BannerPromotion, BannerThemeVars } from "../types";
import { injectStyles, mergeTheme } from "./styles";
import { buildTrackingLink } from "./tracking";
import { setBannerShown } from "../storage/banner";

const WIDGET_ID = "sw-banner";

const BREAKPOINT_MEDIA: Record<string, string> = {
  wide: "(min-width: 1440px)",
  desktop: "(min-width: 1025px)",
  tablet: "(min-width: 769px)",
};

const BREAKPOINT_ORDER = ["wide", "desktop", "tablet"] as const;

let container: HTMLElement | null = null;
let timerId: ReturnType<typeof setTimeout> | null = null;
let keydownHandler: ((e: KeyboardEvent) => void) | null = null;

function findImageForBreakpoint(
  images: BannerImage[],
  breakpoint: string,
): BannerImage | undefined {
  const direct = images.find((i) => i.breakpoint === breakpoint);
  if (direct?.file) return direct;

  const fallbackOrder: Record<string, string[]> = {
    wide: ["desktop", "tablet", "mobile"],
    desktop: ["wide", "tablet", "mobile"],
    tablet: ["desktop", "wide", "mobile"],
  };

  for (const fb of fallbackOrder[breakpoint] ?? []) {
    const img = images.find((i) => i.breakpoint === fb);
    if (img?.file) return img;
  }
  return undefined;
}

function createResponsivePicture(
  images: BannerImage[],
  altText: string,
): HTMLElement {
  const picture = document.createElement("picture");

  for (const breakpoint of BREAKPOINT_ORDER) {
    const img = findImageForBreakpoint(images, breakpoint);
    if (img?.file) {
      const source = document.createElement("source");
      source.media = BREAKPOINT_MEDIA[breakpoint];
      source.srcset = img.file;
      picture.appendChild(source);
    }
  }

  const fallback =
    images.find((i) => i.breakpoint === "mobile") ?? images[0];
  const img = document.createElement("img");
  img.className = "sw-image";
  img.alt = altText;
  img.src = fallback.file;
  picture.appendChild(img);

  return picture;
}

function createCloseButton(): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.className = "sw-close";
  btn.setAttribute("aria-label", "Close banner");
  btn.type = "button";

  const ns = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(ns, "svg");
  svg.setAttribute("width", "20");
  svg.setAttribute("height", "20");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("aria-hidden", "true");
  const path = document.createElementNS(ns, "path");
  path.setAttribute("d", "M6 6l12 12M6 18L18 6");
  path.setAttribute("stroke", "currentColor");
  path.setAttribute("stroke-width", "2");
  path.setAttribute("stroke-linecap", "round");
  svg.appendChild(path);
  btn.appendChild(svg);

  return btn;
}

export function open(
  promotion: BannerPromotion,
  bannerConfig?: BannerConfig,
  theme?: BannerThemeVars,
): void {
  if (container) return; // already open

  const mergedTheme = mergeTheme(theme);
  injectStyles(mergedTheme);

  const dismissible = bannerConfig?.dismissible ?? true;
  const showTitle = bannerConfig?.showTitle ?? false;
  const showDescription = bannerConfig?.showDescription ?? false;
  const remember = bannerConfig?.remember ?? true;

  container = document.createElement("div");
  container.className = "sw-root";
  container.id = WIDGET_ID;
  container.setAttribute("hidden", "");
  container.setAttribute("role", "dialog");
  container.setAttribute("aria-modal", "true");
  container.setAttribute("aria-labelledby", `${WIDGET_ID}-title`);
  container.style.zIndex = String(mergedTheme.zIndex);

  const overlay = document.createElement("div");
  overlay.className = "sw-overlay";
  overlay.tabIndex = -1;

  const dialog = document.createElement("div");
  dialog.className = "sw-dialog";
  dialog.setAttribute("role", "document");

  // Close button
  let closeBtn: HTMLButtonElement | null = null;
  if (dismissible) {
    closeBtn = createCloseButton();
    closeBtn.addEventListener("click", close);
    dialog.appendChild(closeBtn);
    overlay.addEventListener("click", close);
  }

  // Title
  if (showTitle && promotion.name) {
    const header = document.createElement("div");
    header.className = "sw-header";
    const title = document.createElement("h2");
    title.id = `${WIDGET_ID}-title`;
    title.className = "sw-title";
    title.textContent = promotion.name;
    header.appendChild(title);
    dialog.appendChild(header);
  }

  // Body
  const body = document.createElement("div");
  body.className = "sw-body";

  if (promotion.images?.length > 0) {
    const picture = createResponsivePicture(promotion.images, promotion.name);
    const ctaUrl = promotion.link;
    if (ctaUrl) {
      const finalUrl = buildTrackingLink(ctaUrl, promotion.trackingMetadata);
      const imageLink = document.createElement("a");
      imageLink.href = finalUrl;
      imageLink.target = "_blank";
      imageLink.rel = "noopener noreferrer";
      imageLink.className = "sw-image-link";
      imageLink.appendChild(picture);
      body.appendChild(imageLink);
    } else {
      body.appendChild(picture);
    }
  }

  if (showDescription && promotion.description) {
    const desc = document.createElement("p");
    desc.className = "sw-text";
    desc.textContent = promotion.description;
    body.appendChild(desc);
  }

  dialog.appendChild(body);
  dialog.addEventListener("click", (e) => e.stopPropagation());

  container.appendChild(overlay);
  container.appendChild(dialog);
  document.body.appendChild(container);

  if (remember) {
    setBannerShown(promotion.id, promotion.updatedAt ?? new Date().toISOString());
  }

  // Show with animation
  requestAnimationFrame(() => {
    container?.removeAttribute("hidden");
    container?.setAttribute("data-state", "enter");
    const imageLink = container?.querySelector(".sw-image-link");
    (imageLink as HTMLElement | null)?.focus() ??
      closeBtn?.focus() ??
      dialog.focus();
  });

  // Keyboard handling
  keydownHandler = (e: KeyboardEvent) => {
    if (!container || container.hasAttribute("hidden")) return;
    if (e.key === "Escape" && dismissible) close();
    if (e.key === "Tab" && container) {
      const focusables = container.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      const list = Array.from(focusables);
      if (!list.length) return;
      const first = list[0];
      const last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        last.focus();
        e.preventDefault();
      } else if (!e.shiftKey && document.activeElement === last) {
        first.focus();
        e.preventDefault();
      }
    }
  };
  document.addEventListener("keydown", keydownHandler);
}

export function close(): void {
  if (!container) return;

  container.setAttribute("data-state", "exit");
  const ref = container;

  setTimeout(() => {
    ref.setAttribute("hidden", "");
    ref.remove();
  }, 240);

  if (keydownHandler) {
    document.removeEventListener("keydown", keydownHandler);
    keydownHandler = null;
  }
  if (timerId) {
    clearTimeout(timerId);
    timerId = null;
  }
  container = null;
}

export function isOpen(): boolean {
  return container !== null;
}
